/**
 * Top-level navigation tree.
 *
 * Web → mobile route mapping:
 *   /                 → MainTabs > Home
 *   /guides           → MainTabs > Guides
 *   /guides/:id       → MainTabs > Guides > GuideDetail
 *   /diagnose         → MainTabs > Diagnose
 *   /garage           → MainTabs > Garage
 *   /shop/signup      → Shop > Signup
 *   /shop/dashboard   → Shop > Dashboard
 *   /invest           → Invest (modal/standalone)
 *
 * The shop portal lives in its own native-stack so a shop owner can sign up
 * → land in dashboard without seeing the consumer tabs underneath.
 */
import React from 'react';
import { Pressable, Text } from 'react-native';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';

import { useAuth } from '../lib/auth';
import HomeScreen from '../screens/HomeScreen';
import GuidesScreen from '../screens/GuidesScreen';
import GuideDetailScreen from '../screens/GuideDetailScreen';
import DiagnoseScreen from '../screens/DiagnoseScreen';
import GarageScreen from '../screens/GarageScreen';
import VehicleSelectorScreen from '../screens/VehicleSelectorScreen';
import ShopSignupScreen from '../screens/ShopSignupScreen';
import ShopDashboardScreen from '../screens/ShopDashboardScreen';
import InvestScreen from '../screens/InvestScreen';

import { IconCar, IconGarage, IconSteps, IconDiagnose } from '../components/Icons';
import { colors } from '../lib/theme';

const RootStack = createNativeStackNavigator();
const Tabs = createBottomTabNavigator();

function SignOutButton() {
  const { signOut } = useAuth();
  return (
    <Pressable
      onPress={signOut}
      hitSlop={8}
      style={({ pressed }) => ({ paddingHorizontal: 12, opacity: pressed ? 0.6 : 1 })}
    >
      <Text style={{ color: '#fff', fontSize: 13, fontWeight: '600' }}>Sign out</Text>
    </Pressable>
  );
}

function MainTabs() {
  return (
    <Tabs.Navigator
      screenOptions={{
        headerStyle: { backgroundColor: colors.navyDark },
        headerTitleStyle: { color: '#fff', fontWeight: '700' },
        headerTintColor: '#fff',
        tabBarStyle: {
          backgroundColor: colors.navyDark,
          borderTopColor: 'rgba(255,255,255,0.06)',
          height: 64,
          paddingTop: 6,
          paddingBottom: 8,
        },
        tabBarActiveTintColor: colors.btnPrimary,
        tabBarInactiveTintColor: 'rgba(255,255,255,0.5)',
        tabBarLabelStyle: { fontSize: 11, fontWeight: '600' },
      }}
    >
      <Tabs.Screen
        name="Home"
        component={HomeScreen}
        options={{
          title: 'GarageIQ',
          tabBarIcon: ({ size }) => <IconCar size={size} />,
          headerRight: () => <SignOutButton />,
        }}
      />
      <Tabs.Screen
        name="Guides"
        component={GuidesScreen}
        options={{ tabBarIcon: ({ size }) => <IconSteps size={size} /> }}
      />
      <Tabs.Screen
        name="Diagnose"
        component={DiagnoseScreen}
        options={{ tabBarIcon: ({ size }) => <IconDiagnose size={size} /> }}
      />
      <Tabs.Screen
        name="Garage"
        component={GarageScreen}
        options={{ tabBarIcon: ({ size }) => <IconGarage size={size} /> }}
      />
    </Tabs.Navigator>
  );
}

export default function RootNavigator() {
  return (
    <RootStack.Navigator
      screenOptions={{
        headerStyle: { backgroundColor: colors.navyDark },
        headerTitleStyle: { color: '#fff', fontWeight: '700' },
        headerTintColor: '#fff',
        contentStyle: { backgroundColor: colors.appBackground },
      }}
    >
      <RootStack.Screen
        name="MainTabs"
        component={MainTabs}
        options={{ headerShown: false }}
      />
      <RootStack.Screen
        name="GuideDetail"
        component={GuideDetailScreen}
        // Title set dynamically by the screen.
        options={{ title: '' }}
      />
      <RootStack.Screen
        name="VehicleSelector"
        component={VehicleSelectorScreen}
        options={{ title: 'Choose Vehicle', presentation: 'modal' }}
      />
      <RootStack.Screen
        name="Invest"
        component={InvestScreen}
        options={{ title: 'For Investors' }}
      />
      <RootStack.Screen
        name="ShopSignup"
        component={ShopSignupScreen}
        options={{ title: 'Shop Sign-Up' }}
      />
      <RootStack.Screen
        name="ShopDashboard"
        component={ShopDashboardScreen}
        options={{ title: 'Shop Dashboard' }}
      />
    </RootStack.Navigator>
  );
}
